import { db } from "@/mixins/firebase";
import { themes } from "@/themes/register";
import { updatePreset, usePreset } from "@primeuix/themes";
import { doc, onSnapshot, Unsubscribe } from "firebase/firestore";
import { defineStore } from "pinia";
import { computed, readonly, ref, watch } from "vue";
import { ThemePreset } from "@/themes/preset";
import {
  PrivateConfig,
  PrivateConfigFeatures,
  PublicConfig,
  PublicConfigStatic,
} from "@mimsights-shared/types";
import {
  privateConfigConverter,
  publicConfigConverter,
} from "@mimsights-shared/converters/frontend";
import { featuresForEntity, withoutNotInheritedFeatures } from "@mimsights-shared/entityFeatures";
import type { OrgAndCustShared } from "@mimsights-shared/types";

const STORAGE_CONF_KEY = "conf";
const STORAGE_CONF_USER_KEY = "conf_user";
const STORAGE_THEME_KEY = "conf_theme";
const STORAGE_SITENAME_KEY = "conf_sitename";

export const useOrganizationConfig = defineStore("organizationConfig", () => {
  const testingConfigs = ref<{
    private: PrivateConfig;
    public: PublicConfig;
  }>();
  const features = ref<Partial<PrivateConfigFeatures>>({});
  const staticData = ref<Partial<PublicConfigStatic>>({});
  // The signed-in user's entity, live: which of the root's features it gets depends on its type
  // and place in the tree (featuresForEntity). Set by the entity store; reset() leaves it alone
  // because load() resets too.
  const entity = ref<Pick<OrgAndCustShared, "type" | "ancestors"> | null>(null);
  // A config loaded for someone outside the tree (the QR code pages): no root-only features
  const outsideTree = ref(false);
  const entityFeatures = computed(() =>
    outsideTree.value
      ? withoutNotInheritedFeatures(features.value)
      : featuresForEntity(features.value, entity.value),
  );

  const currentId = ref<string | null>(null);
  const publicLoaded = ref(false);
  const privateLoaded = ref(false);

  const preloadedConfigTheme = ref<string>();
  const preloadedConfigSitename = ref<string>();

  const unsubs = ref<{ public: Unsubscribe | null; private: Unsubscribe | null }>({
    public: null,
    private: null,
  });

  // ── Internal helpers ──────────────────────────────────────────

  function mergeConfig(data: PublicConfig | PrivateConfig) {
    features.value = { ...features.value, ...(data?.features || {}) };
    staticData.value = { ...staticData.value, ...(data?.static || {}) };
  }

  function subscribeToPrivateConfig(id: string, cb?: () => void) {
    unsubs.value.private?.();
    const configRef = doc(db, "private_config", id).withConverter(privateConfigConverter);
    try {
      unsubs.value.private = onSnapshot(
        configRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            cb?.();
            return;
          }
          mergeConfig(snapshot.data());

          cb?.();
        },
        // A failed listener must still mark the config as loaded, or the router guard waits forever
        (error) => {
          console.error("Unable to load private config", error);
          cb?.();
        },
      );
    } catch (error) {
      console.error("Unable to load private config", error);
      cb?.();
    }
  }

  function subscribeToPublicConfig(id: string, cb?: () => void) {
    unsubs.value.public?.();
    const configRef = doc(db, "public_config", id).withConverter(publicConfigConverter);
    try {
      unsubs.value.public = onSnapshot(
        configRef,
        (snapshot) => {
          if (snapshot.exists()) {
            mergeConfig(snapshot.data());
          }
          cb?.();
        },
        (error) => {
          console.error("Unable to load public config", error);
          cb?.();
        },
      );
    } catch (error) {
      console.error("Unable to load public config", error);
      cb?.();
    }
  }

  function unsubscribeAll() {
    unsubs.value.public?.();
    unsubs.value.private?.();
    unsubs.value = { public: null, private: null };
  }

  function removePersistentStorage() {
    localStorage.removeItem(STORAGE_CONF_KEY);
    localStorage.removeItem(STORAGE_CONF_USER_KEY);
    localStorage.removeItem(STORAGE_SITENAME_KEY);
    localStorage.removeItem(STORAGE_THEME_KEY);
  }

  function findStoredConfigId() {
    const localStorageConfigId = localStorage.getItem(STORAGE_CONF_KEY);
    if (localStorageConfigId) {
      return localStorageConfigId;
    }
    const queryParams = new URLSearchParams(window.location.search);
    const queryParamsConfigId = queryParams.get(STORAGE_CONF_KEY);
    if (queryParamsConfigId) {
      localStorage.setItem(STORAGE_CONF_KEY, queryParamsConfigId);
      return queryParamsConfigId;
    }
  }

  function preloadConfigTheme() {
    const localStorageConfigTheme = localStorage.getItem(STORAGE_THEME_KEY);
    if (localStorageConfigTheme) {
      preloadedConfigTheme.value = localStorageConfigTheme;
    }
    const localStorageConfigSiteName = localStorage.getItem(STORAGE_SITENAME_KEY);
    if (localStorageConfigSiteName) {
      preloadedConfigSitename.value = localStorageConfigSiteName;
    }
    setTheme(localStorageConfigTheme || undefined, localStorageConfigSiteName || undefined);
  }
  // apply some configs directly
  watch(
    () => {
      return {
        siteName: staticData.value.siteName,
        theme: staticData.value.theme,
        publicLoaded: publicLoaded.value,
      };
    },
    ({ siteName, theme, publicLoaded }, old) => {
      if (!publicLoaded) return;

      if (siteName == old.siteName && theme == old.theme) return;
      setTheme(theme, siteName);
      if (staticData.value.theme) {
        localStorage.setItem(STORAGE_THEME_KEY, staticData.value.theme);
      }
      if (staticData.value.siteName) {
        localStorage.setItem(STORAGE_SITENAME_KEY, staticData.value.siteName);
      }
    },
  );
  // ── Reset ─────────────────────────────────────────────────────

  function reset(opt: { loaded?: boolean } = { loaded: false }) {
    features.value = {};
    staticData.value = {};
    outsideTree.value = false;
    unsubscribeAll();
    publicLoaded.value = opt.loaded || false;
    privateLoaded.value = opt.loaded || false;
    currentId.value = null;
    preloadedConfigSitename.value = undefined;
    preloadedConfigTheme.value = undefined;
  }

  // ── Public API ────────────────────────────────────────────────

  function setEntity(value: Pick<OrgAndCustShared, "type" | "ancestors"> | null) {
    entity.value = value ? { type: value.type, ancestors: value.ancestors } : null;
  }

  /** `outsideTree`: the viewer isn't a member of this config's tree (the QR code pages). */
  async function load(id?: string | null, opt: { outsideTree?: boolean } = {}) {
    reset();
    outsideTree.value = !!opt.outsideTree;
    preloadConfigTheme();
    if (!id) {
      privateLoaded.value = true;
      publicLoaded.value = true;
      return;
    }

    features.value = {};
    staticData.value = {};
    await loadPublic(id, true);

    currentId.value = id;

    if (privateLoaded.value) {
      return;
    }
    subscribeToPrivateConfig(id, () => {
      privateLoaded.value = true;
    });
  }

  async function loadPublic(id?: string, skipReset?: boolean) {
    if (!skipReset) {
      reset();
    }
    preloadConfigTheme();
    if (id) {
      localStorage.setItem(STORAGE_CONF_KEY, id);
    }
    if (!id) {
      id = findStoredConfigId();
      if (!id) {
        publicLoaded.value = true;
        return;
      }
    }
    subscribeToPublicConfig(id, () => {
      publicLoaded.value = true;
    });
  }

  function startTestConfigs(privateConifg: PrivateConfig, publicConfig: PublicConfig) {
    testingConfigs.value = {
      private: privateConifg,
      public: publicConfig,
    };
    currentId.value = privateConifg.id;
    mergeConfig(publicConfig);
    mergeConfig(privateConifg);
  }
  function endTestConfigs() {
    testingConfigs.value = undefined;
    reset();
  }
  const isTestingConfig = computed(() => {
    return !!testingConfigs.value;
  });

  type FeatureKey = keyof typeof features.value;

  function getFeature<K extends FeatureKey>(
    key: K,
    opt: {
      default: (typeof features.value)[K];
    },
  ): Exclude<(typeof features.value)[K], undefined | null>;
  function getFeature<K extends FeatureKey>(
    key: K,
    opt?: {
      default?: (typeof features.value)[K];
    },
  ): (typeof features.value)[K];
  function getFeature<K extends FeatureKey>(
    key: K,
    opt: {
      default?: (typeof features.value)[K];
    } = {},
  ) {
    if (key in entityFeatures.value) {
      return entityFeatures.value[key];
    }

    return opt?.default;
  }

  type StaticDataKey = keyof typeof staticData.value;
  type StaticDataValue = (typeof staticData.value)[StaticDataKey];
  type SafeStaticDataValue = Exclude<StaticDataValue, undefined | null>;

  function getStatic<R = SafeStaticDataValue>(
    key: StaticDataKey,
    opt: {
      default: StaticDataValue;
      transform?: (value: SafeStaticDataValue) => R;
    },
  ): R;
  function getStatic<R = SafeStaticDataValue | undefined>(
    key: StaticDataKey,
    opt?: {
      default?: StaticDataValue;
      transform?: (value: SafeStaticDataValue) => R;
    },
  ): R;
  function getStatic(
    key: StaticDataKey,
    opt: {
      default?: StaticDataValue;
      transform?: (value: SafeStaticDataValue) => any;
    } = {},
  ) {
    if (key in staticData.value) {
      const value = staticData.value[key];
      if (value != undefined) {
        if (opt?.transform) {
          return opt.transform(value);
        }
        return value;
      }
    }
    return opt?.default;
  }

  // ── Init ──────────────────────────────────────────────────────

  loadPublic();

  return {
    currentId: readonly(currentId),
    publicLoaded: readonly(publicLoaded),
    privateLoaded: readonly(privateLoaded),
    outsideTree: readonly(outsideTree),
    load,
    loadPublic,
    setEntity,
    getFeature,
    getStatic,
    removePersistentStorage,
    reset,
    test: {
      start: startTestConfigs,
      end: endTestConfigs,
      isTesting: isTestingConfig,
      configs: testingConfigs,
    },
  };
});

// TODO: Move favicons to config
const themeConfig: Record<string, { light: string; dark: string }> = {
  epro: {
    light: "/epro/fav_blue.png",
    dark: "/epro/fav_white.png",
  },
};
/** Set the theme, if not provided clean up */
export function setTheme(theme?: string, title?: string) {
  const updateFavIcon = (theme?: string) => {
    const config = themeConfig[theme ?? ""];
    const light = config?.light ?? "/favicon.ico";
    const dark = config?.dark ?? "/favicon.ico";
    document.getElementById("favicon-light")?.setAttribute("href", light);
    document.getElementById("favicon-dark")?.setAttribute("href", dark);
  };
  const updatePrimevueTheme = (theme: string | null | undefined) => {
    if (!theme) {
      usePreset(ThemePreset);
      return;
    }
    const primevueTheme = themes?.[theme];
    if (!primevueTheme) {
      usePreset(ThemePreset);
      return;
    }
    updatePreset(primevueTheme);
  };

  const updateTitle = (title?: string) => {
    document.title = title || "Mimsights";
  };

  const element = document.documentElement;
  if (theme) {
    element.setAttribute("data-theme", theme);
  } else {
    element.removeAttribute("data-theme");
  }

  updatePrimevueTheme(theme);
  updateFavIcon(theme);
  updateTitle(title);
}
